import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { getVideoById, categories } from '@/mocks/videos';
import { getArticleById, getRelatedArticles } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();

  const video = id ? getVideoById(id) : undefined;
  const article = id ? getArticleById(id) : undefined;

  const item = video || article;

  if (!item) {
    return (
      <>
        <ScrollToTop />
        <div className="min-h-screen bg-background-50">
          <Navbar />
          <main className="pt-16">
            <div className="max-w-4xl mx-auto px-4 md:px-6 py-20 text-center">
              <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100">
                <i className="ri-error-warning-line text-2xl text-foreground-400"></i>
              </div>
              <h1 className="mt-4 text-xl font-bold text-foreground-950">内容未找到</h1>
              <p className="mt-2 text-sm text-foreground-500">您访问的内容不存在或已被移除</p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 mt-6 px-5 py-2.5 text-sm font-medium text-background-50 bg-primary-500 hover:bg-primary-600 rounded-full transition-colors cursor-pointer whitespace-nowrap"
              >
                返回首页
              </Link>
            </div>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  const isVideo = 'duration' in item;
  const categorySlug = item.category;
  const categoryName = categories.find((c) => c.slug === categorySlug)?.name || categorySlug;

  const relatedArticles = getRelatedArticles(id || '');

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: item.title,
    description: 'description' in item ? item.description : item.title,
    image: 'thumbnail' in item ? item.thumbnail : (item as { coverImage: string }).coverImage,
    datePublished: item.publishDate,
    author: {
      '@type': 'Organization',
      name: '久久视频',
    },
    publisher: {
      '@type': 'Organization',
      name: '久久视频',
    },
  };

  const videoJsonLd = isVideo
    ? {
        '@context': 'https://schema.org',
        '@type': 'VideoObject',
        name: item.title,
        description: ('description' in item ? item.description : ''),
        thumbnailUrl: 'thumbnail' in item ? item.thumbnail : '',
        uploadDate: item.publishDate,
        duration: ('duration' in item ? `PT${item.duration.replace(/:/g, 'M')}S`.replace('PT0', 'PT').replace(':', 'M') : ''),
      }
    : null;

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.jiujiu-video.com/' },
      { '@type': 'ListItem', position: 2, name: categoryName, item: `https://www.jiujiu-video.com/category/${categorySlug}` },
      { '@type': 'ListItem', position: 3, name: item.title },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
      />
      {videoJsonLd && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(videoJsonLd) }}
        />
      )}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <div className="max-w-4xl mx-auto px-4 md:px-6 py-8 md:py-12">
            {/* Breadcrumb */}
            <nav aria-label="面包屑导航" className="mb-6">
              <ol className="flex items-center gap-2 text-xs text-foreground-500 flex-wrap">
                <li>
                  <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">
                    首页
                  </Link>
                </li>
                <li>
                  <i className="ri-arrow-right-s-line"></i>
                </li>
                <li>
                  <Link to={`/category/${categorySlug}`} className="hover:text-primary-500 transition-colors cursor-pointer">
                    {categoryName}
                  </Link>
                </li>
                <li>
                  <i className="ri-arrow-right-s-line"></i>
                </li>
                <li className="text-foreground-950 font-medium truncate max-w-[200px]">{item.title}</li>
              </ol>
            </nav>

            {/* Article Header */}
            <article>
              <header className="mb-8">
                <div className="flex flex-wrap items-center gap-2 mb-4">
                  {item.tags.map((tag: string) => (
                    <span
                      key={tag}
                      className="px-2.5 py-0.5 text-xs font-medium text-primary-600 bg-primary-100 rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-950 leading-tight">
                  {item.title}
                </h1>
                <div className="flex flex-wrap items-center gap-4 mt-4 text-xs text-foreground-500">
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line"></i>
                    {item.publishDate}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i>
                    {item.views}
                    {'readTime' in item ? ' 阅读' : ' 播放'}
                  </span>
                  {isVideo && (
                    <span className="flex items-center gap-1 px-2 py-0.5 bg-accent-100 text-accent-700 rounded-md">
                      <i className="ri-vidicon-line"></i>
                      视频
                    </span>
                  )}
                  {'readTime' in item && (
                    <span className="flex items-center gap-1">
                      <i className="ri-time-line"></i>
                      {item.readTime}
                    </span>
                  )}
                </div>
              </header>

              {/* Cover Image or Video Player */}
              <div className="mb-8 rounded-lg overflow-hidden">
                {'thumbnail' in item ? (
                  <div className="relative aspect-video bg-background-900">
                    <img
                      src={item.thumbnail}
                      alt={item.title}
                      title={`${item.title} - 久久视频高清在线观看`}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 flex items-center justify-center bg-black/40 cursor-pointer group">
                      <div className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center rounded-full bg-primary-500/90 group-hover:bg-primary-500 transition-colors">
                        <i className="ri-play-fill text-3xl md:text-4xl text-background-50"></i>
                      </div>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
                      <div className="flex items-center gap-3 text-xs text-foreground-200">
                        <span>{item.views} 次播放</span>
                        <span>{item.duration}</span>
                        <span className="flex items-center gap-1">
                          <i className="ri-star-fill text-accent-400"></i>
                          {item.rating}
                        </span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <img
                    src={(item as { coverImage: string }).coverImage}
                    alt={item.title}
                    title={item.title}
                    className="w-full rounded-lg object-cover"
                  />
                )}
              </div>

              {/* Description */}
              {'description' in item && item.description && (
                <div className="mb-6 p-5 rounded-lg bg-background-100 border border-background-200/70">
                  <p className="text-sm text-foreground-700 leading-relaxed">{item.description}</p>
                </div>
              )}

              {/* Article Content */}
              {'content' in item && item.content && (
                <div
                  className="prose prose-sm max-w-none text-foreground-800 leading-relaxed
                    [&>h3]:text-lg [&>h3]:font-heading [&>h3]:font-bold [&>h3]:text-foreground-950 [&>h3]:mt-8 [&>h3]:mb-4
                    [&>p]:text-sm [&>p]:leading-relaxed [&>p]:mb-4 [&>p]:text-foreground-700
                    [&>strong]:text-foreground-950"
                  dangerouslySetInnerHTML={{ __html: item.content }}
                />
              )}
            </article>

            {/* Related Articles */}
            {relatedArticles.length > 0 && (
              <aside className="mt-14 pt-10 border-t border-background-200/70">
                <h3 className="text-lg font-heading font-bold text-foreground-950 mb-6">
                  相关推荐
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {relatedArticles.map((rel) => (
                    <Link
                      key={rel.id}
                      to={`/article/${rel.id}`}
                      className="group block rounded-lg overflow-hidden bg-background-100 hover:bg-background-200/70 transition-all duration-300 cursor-pointer"
                    >
                      <div className="aspect-[16/9] overflow-hidden">
                        <img
                          src={rel.coverImage}
                          alt={rel.title}
                          title={rel.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-4">
                        <h4 className="text-sm font-medium text-foreground-950 line-clamp-2 group-hover:text-primary-600 transition-colors leading-snug">
                          {rel.title}
                        </h4>
                        <div className="flex items-center gap-2 mt-2 text-xs text-foreground-500">
                          <span>{rel.readTime}</span>
                          <span>·</span>
                          <span>{rel.views} 阅读</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </aside>
            )}
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}